import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Plus, Edit2, Trash2, Eye, Search, X, Check, FileText } from "lucide-react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getTicketTypeConfig } from "../../lib/utils";
import { toast } from "sonner";

const TICKET_TYPES_FOR_KB = [
    "incident", "problem", "change", "service_request", "access_request",
    "software_install", "software_update", "new_equipment", "onboarding", "offboarding",
    "security", "question", "other", "password_reset", "defective_equipment", "equipment_loan",
];

function ArticleEditor({ article, onSave, onCancel }) {
    const [title, setTitle] = useState(article?.title || "");
    const [content, setContent] = useState(article?.content || "");
    const [summary, setSummary] = useState(article?.summary || "");
    const [status, setStatus] = useState(article?.status || "draft");
    const [types, setTypes] = useState(article?.related_ticket_types || []);
    const [tagsInput, setTagsInput] = useState((article?.tags || []).join(", "));
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);

    const handleSave = async () => {
        if (!title.trim()) { toast.error("Le titre est requis"); return; }
        setSaving(true);
        try {
            const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
            await onSave({
                title: title.trim(),
                content,
                summary: summary.trim(),
                status,
                related_ticket_types: types,
                tags,
            });
        } finally {
            setSaving(false);
        }
    };

    const toggleType = (t) => {
        setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    };

    return (
        <div className="space-y-4" data-testid="article-editor">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{article ? "Modifier l'article" : "Nouvel article"}</h2>
                <div className="flex items-center gap-2">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid="article-status-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="draft">Brouillon</SelectItem>
                            <SelectItem value="published">Publié</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={onCancel}>Annuler</Button>
                    <Button size="sm" onClick={handleSave} disabled={saving || !title.trim()} data-testid="save-article-btn">
                        <Check className="w-3.5 h-3.5 mr-1" />
                        {saving ? "..." : "Enregistrer"}
                    </Button>
                </div>
            </div>

            <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre de l'article"
                className="text-base font-medium"
                data-testid="article-title-input"
            />

            <Input
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Résumé court (1-2 phrases)"
                className="text-sm"
                data-testid="article-summary-input"
            />

            {/* Tags */}
            <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Tags (séparés par des virgules)"
                className="text-sm"
                data-testid="article-tags-input"
            />

            {/* Related types */}
            <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Types de tickets associés</p>
                <div className="flex flex-wrap gap-1.5">
                    {TICKET_TYPES_FOR_KB.map((t) => {
                        const cfg = getTicketTypeConfig(t);
                        const selected = types.includes(t);
                        return (
                            <button
                                key={t}
                                onClick={() => toggleType(t)}
                                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${selected ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"}`}
                                data-testid={`article-type-${t}`}
                            >
                                {cfg.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content editor with preview toggle */}
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <button
                        onClick={() => setPreview(false)}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${!preview ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Éditer
                    </button>
                    <button
                        onClick={() => setPreview(true)}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${preview ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Aperçu
                    </button>
                </div>
                {!preview ? (
                    <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Contenu de l'article en Markdown..."
                        className="min-h-[400px] font-mono text-sm"
                        data-testid="article-content-input"
                    />
                ) : (
                    <div
                        className="min-h-[400px] p-4 border rounded-md bg-white prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(content || "*Aucun contenu*")) }}
                        data-testid="article-preview"
                    />
                )}
            </div>
        </div>
    );
}

export default function AdminKnowledgeBase() {
    const { currentUser, getUserById } = useApp();
    const [searchParams, setSearchParams] = useSearchParams();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | article object
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    // Handle incoming URL params for pre-filled article creation
    useEffect(() => {
        if (searchParams.get("new") === "1") {
            const prefilled = {
                title: searchParams.get("title") || "",
                content: searchParams.get("content") || "",
                related_ticket_types: searchParams.get("type") ? [searchParams.get("type")] : [],
                source_ticket_id: searchParams.get("source") || null,
                status: "draft",
            };
            setEditing({ ...prefilled, _prefilled: true });
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const loadArticles = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterStatus !== "all") params.status = filterStatus;
            const data = await api.getKBArticles(params);
            setArticles(data);
        } catch {
            toast.error("Erreur lors du chargement des articles");
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { loadArticles(); }, [loadArticles]);

    const handleSave = async (data) => {
        try {
            if (editing === "new" || editing?._prefilled) {
                const payload = { ...data, created_by: currentUser.id };
                if (editing?.source_ticket_id) payload.source_ticket_id = editing.source_ticket_id;
                await api.createKBArticle(payload);
                toast.success("Article créé");
            } else {
                await api.updateKBArticle(editing.id, data);
                toast.success("Article mis à jour");
            }
            setEditing(null);
            loadArticles();
        } catch {
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        try {
            await api.deleteKBArticle(id);
            toast.success("Article supprimé");
            loadArticles();
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    const filtered = articles.filter((a) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return a.title.toLowerCase().includes(q) || (a.tags || []).some((t) => t.toLowerCase().includes(q));
    });

    if (editing) {
        return (
            <div className="max-w-4xl">
                <ArticleEditor
                    article={editing === "new" ? null : editing._prefilled ? editing : editing}
                    onSave={handleSave}
                    onCancel={() => setEditing(null)}
                />
            </div>
        );
    }

    return (
        <div data-testid="admin-kb-page">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <BookOpen className="w-6 h-6" />
                        Base de connaissances
                    </h1>
                    <p className="page-subtitle">{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
                </div>
                <Button onClick={() => setEditing("new")} data-testid="new-article-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel article
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher..."
                        className="pl-9 h-9"
                        data-testid="kb-search-input"
                    />
                </div>
                <div className="flex gap-1">
                    {["all", "published", "draft"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${filterStatus === s ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                            data-testid={`kb-filter-${s}`}
                        >
                            {s === "all" ? "Tous" : s === "published" ? "Publiés" : "Brouillons"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Articles list */}
            {loading ? (
                <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun article trouvé</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((article) => {
                        const author = getUserById(article.created_by);
                        return (
                            <div
                                key={article.id}
                                className="card hover:border-gray-300 transition-colors cursor-pointer"
                                onClick={() => setEditing(article)}
                                data-testid={`kb-article-${article.id}`}
                            >
                                <div className="card-body flex items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-sm font-semibold text-gray-900 truncate">{article.title}</h3>
                                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${article.status === "published" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                                {article.status === "published" ? "Publié" : "Brouillon"}
                                            </span>
                                        </div>
                                        {article.summary && (
                                            <p className="text-xs text-gray-500 truncate">{article.summary}</p>
                                        )}
                                        <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                                            {author && <span>{author.first_name} {author.last_name}</span>}
                                            <span>{article.views} vue{article.views !== 1 ? "s" : ""}</span>
                                            <span>
                                                {article.helpful_count} utile{article.helpful_count !== 1 ? "s" : ""}
                                            </span>
                                            {(article.tags || []).length > 0 && (
                                                <span>{article.tags.join(", ")}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setEditing(article); }}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(article.id); }}
                                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                                            title="Supprimer"
                                            data-testid={`delete-article-${article.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
